import { v4 as uuidv4 } from 'uuid';
import { EventTypes } from 'redux-segment';
import instance from 'Shared/api/instance';
import has from 'lodash/has';
import get from 'lodash/get';
import last from 'lodash/last';
import range from 'lodash/range';
import uniqBy from 'lodash/uniqBy';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { getItemsByUuids, getItemsForStandardViewWithMeetingMode } from 'Shared/selectors/workspace';
import { setOptionsForInviteToTeammateModal } from 'Shared/actions/optionsForInviteToTeammateModal';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { editorStateForEditorComponent, jsonEmptyContentState } from 'Shared/utils/draftJsHelpers';
import * as api from 'Shared/api';
import { createItem } from 'Shared/api/items';
import * as MeetingItemsAPI from 'Shared/api/MeetingItems.api';
import { listFromType } from 'Shared/reducers/workspace';
import {
  handleError,
  getAssignmentAttributes,
  generateMeetingItemsByDoc,
  convertFileToBase64,
  showNotificationError,
} from 'Shared/helpers';
import { generateTempItem, generateFormDataFromItem, itemWithFieldsForCreate } from 'Shared/helpers/items';
import { getSourcePage } from 'Shared/utils/getSourcePage';
import { getPositionFieldForItem } from 'Shared/utils/getPositionField';
import { tr } from 'Shared/utils/hooks/use-translation';

// TODO: I'm proposing to deprecate NotificationManager here, and instead
// pass error key to dispatch so that our errorMessage can catch it.
const makeRequest = ({ dispatch, endpoint, method, body, success, error }) =>
  api.makeRequest({ endpoint, method, body, success, error }).then(
    response => {
      const json = response.data;
      if (success) success(json);
      return response;
    },
    response => {
      if (error) error(error);
      dispatch(handleError(response));
    }
  );

const buildSortOrder = meetingItems => {
  let index = 1;
  const uuidsToPosition = [];
  const visibleItems = getItemsForStandardViewWithMeetingMode(meetingItems || []);

  visibleItems
    .filter(meetingItem => !meetingItem.parentId)
    .forEach(meetingItem => {
      uuidsToPosition.push({ uuid: meetingItem.uuid, position: index, parentId: meetingItem.parentId });
      index += 1;

      const childrenItems = visibleItems.filter(({ parentId }) => parentId === meetingItem.id);
      if (childrenItems.length > 0) {
        childrenItems.forEach(childMeetingItem => {
          uuidsToPosition.push({ uuid: childMeetingItem.uuid, position: index, parentId: childMeetingItem.parentId });
          index += 1;
        });
      }
    });

  return uuidsToPosition;
};

const uuidsToPositions = ({ workspace }) => ({
  actionItems: buildSortOrder(workspace.meeting_items),
  discussionTopics: buildSortOrder(workspace.discussion_topics),
});

export const fetchAdditionalItems =
  ({
    workspaceId,
    filter = 'all',
    forcedUuid,
    minWeeksAgo = null,
    maxWeeksAgo = null,
    groupBy = null,
    offset = 0,
    limit = 20,
  }) =>
  async dispatch => {
    const requestedTimelineGroups = range(minWeeksAgo, maxWeeksAgo);

    const query = new URLSearchParams({
      workspace_id: String(workspaceId),
      filter_by: String(filter),
      min_weeks_ago: String(minWeeksAgo),
      max_weeks_ago: String(maxWeeksAgo),
      group_by: String(groupBy),
      offset: String(offset),
      limit: String(limit),
      forced_uuid: String(forcedUuid),
    }).toString();

    const response = await makeRequest({
      endpoint: `/api/v0/meeting_items?${query}`,
      method: 'GET',
      dispatch,
      body: {},
      success: json => {
        dispatch({
          type: 'MEETING_ITEMS_SYNC',
          meetingItems: json.data.meeting_items,
          discussionTopics: json.data.discussion_topics,
          deletedMeetingItems: json.data.deleted_meeting_items,
          deletedDiscussionTopics: json.data.deleted_discussion_topics,
          filter,
          requestedTimelineGroups,
          groupBy,
        });
      },
    });
    return response?.data?.data?.countItems;
  };

export const addItem =
  ({
    isShowLoader = false,
    position,
    isSuggestedTopic,
    isFeedbackReminder,
    meetingTranscriptionSlug,
    actionItemKey,
    parentId = null,
    parentUuid = null,
    body = jsonEmptyContentState(),
    workspaceId,
    video,
    itemUuid = uuidv4(),
    itemType = 'action_item',
    positionForActionItemsPage,
    positionForHomePage,
    positionForGoalDetailPage,
    isAssignCurrentUser = true,
    isSection = false,
    assignmentInfo = {},
    attachments = [],
    asanaData = null,
    servicenowData = null,
    associatedMeetingItemId = null,
    dueBy,
    goalSlug = null,
    reviewSlug = null,
  }) =>
  async (dispatch, getState) => {
    const {
      selectedUuids,
      sortedActionItemsUuids,
      sortedDiscussionTopicsUuids,
      workspace: { meeting_items: actionItems, discussion_topics: discussionTopics },
      workspaces,
    } = getState();
    const todos = itemType === 'action_item' ? actionItems : discussionTopics;
    const sortedItems = itemType === 'action_item' ? sortedActionItemsUuids : sortedDiscussionTopicsUuids;
    let workspaceIdParam;
    let сalculatedPosition;
    if (!position) {
      const parentAndChildren = todos
        .filter(({ uuid, parentUuid: thisParentUuid }) => uuid === parentUuid || thisParentUuid === parentUuid)
        .map(i => i.uuid);
      const selectedIncludesParent = parentAndChildren.filter(u => selectedUuids.includes(u)).length > 0;

      if (selectedUuids.length && selectedIncludesParent && !parentUuid) {
        сalculatedPosition =
          get(
            todos.find(({ uuid }) => uuid === last(selectedUuids)),
            'position'
          ) + 1;
        // Otherwise insert after the last existing sibling if trying to add a child item
      } else if (parentUuid) {
        сalculatedPosition =
          get(
            todos.find(({ uuid }) => uuid === last(parentAndChildren)),
            'position'
          ) + 1;
        // Otherwise insert at the end of the list
      } else {
        сalculatedPosition =
          get(
            todos.find(({ uuid }) => uuid === last(sortedItems)),
            'position'
          ) + 1;
      }
    }

    if (workspaceId) {
      workspaceIdParam = workspaceId;
    } else {
      workspaceIdParam = workspaces.find(({ isPersonal }) => isPersonal)?.id;
    }

    const user = getState().user;
    const tempItem = generateTempItem({
      position: position || сalculatedPosition || 0,
      parentId,
      parentUuid,
      body,
      itemUuid,
      isSection,
      isAssignCurrentUser,
      associatedMeetingItemId,
      user,
      itemType,
      workspaceId: workspaceIdParam,
      positionForActionItemsPage,
      positionForHomePage,
      positionForGoalDetailPage,
      assignmentInfo,
      dueBy,
      asanaData,
      servicenowData,
      goalSlug,
      reviewSlug,
      meetingTranscriptionSlug,
    });

    if (video) {
      tempItem.video = {
        url: window.URL.createObjectURL(video.file),
        previewImageUrl: video.preview,
        audioOnly: video.audioOnly,
      };
    }
    if (attachments.length) {
      tempItem.showPreview = true;
      tempItem.attachments = await Promise.all(
        attachments.map(async (attachment, index) => ({
          contentType: attachment.type,
          createdAt: new Date().toISOString(),
          fileName: attachment.name,
          id: index,
          isImage: true,
          type: 'attachment',
          url: await convertFileToBase64(attachment),
        }))
      );
    }

    if (isShowLoader) tempItem.isShowLoader = true;

    dispatch({
      type: 'MEETING_ITEM_ADD_REQUEST',
      meetingItemType: itemType,
      payload: tempItem,
    });

    const data = generateFormDataFromItem(tempItem);
    if (isSuggestedTopic) data.append('item[is_suggested_topic]', isSuggestedTopic);

    if (isFeedbackReminder) data.append('item[is_feedback_reminder]', isFeedbackReminder);

    if (asanaData) {
      data.append('item[asana_data][task_id]', asanaData.taskId);
      data.append('item[asana_data][project_id]', asanaData.projectId);
    }

    if (servicenowData) {
      data.append('item[servicenow_data][sys_id]', servicenowData.sys_id);
      data.append('item[servicenow_data][sys_class_name]', servicenowData.sys_class_name);
    }

    if (isAssignCurrentUser) {
      data.append('item[assignment_attributes][person_id]', user.person.id);
      data.append('item[assignment_attributes][user_id]', user.id);
    } else if (assignmentInfo.assignerUserId && assignmentInfo.assigneePersonId) {
      data.append('item[assignment_attributes][person_id]', assignmentInfo.assigneePersonId);
      data.append('item[assignment_attributes][user_id]', assignmentInfo.assignerUserId);
    }

    data.append('clientSyncToken', getState().app.clientSyncToken);
    data.append('uuidsToPositions', JSON.stringify(uuidsToPositions({ workspace: getState().workspace })));

    if (video) {
      data.append('item[video_attributes][webm_file]', video.file);
      data.append('item[video_attributes][audio_only]', video.audioOnly);
    }

    if (attachments.length) {
      attachments.forEach(attachment => data.append('item[attachments][]', attachment));
    }

    if (goalSlug) data.append('item[goal_slug]', goalSlug);

    if (reviewSlug) data.append('item[review_slug]', reviewSlug);

    if (meetingTranscriptionSlug) data.append('item[meeting_transcription_slug]', meetingTranscriptionSlug);

    if (actionItemKey) data.append('item[action_item_key]', actionItemKey.toLowerCase());

    if (positionForGoalDetailPage) data.append('item[position_for_goal_detail_page]', positionForGoalDetailPage);
    return createItem(data, getSourcePage())
      .then(json => {
        const payload = json.data.data;
        delete payload.body;
        dispatch({
          type: 'MEETING_ITEM_ADD_RESPONSE',
          meetingItemType: itemType,
          payload,
        });
        return json;
      })
      .catch(error => {
        showNotificationError(error);
        dispatch({
          type: 'MEETING_ITEM_ADD_ERROR',
          meetingItemType: itemType,
          payload: { uuid: tempItem.uuid },
        });
        return error;
      });
  };

export const addSubItem =
  ({ itemUuid, body, workspaceId, itemType }) =>
  (dispatch, getState) => {
    const state = getState();
    const workspaceItems =
      itemType === 'action_item' ? state.workspace.meeting_items : state.workspace.discussion_topics;
    const parentItem = workspaceItems?.find(({ uuid }) => uuid === last(state.selectedUuids));

    if (parentItem && parentItem.itemType === itemType) {
      dispatch(
        addItem({
          parentId: parentItem.parentId || parentItem.id,
          parentUuid: parentItem.parentUuid || parentItem.uuid,
          position: parentItem.position + 1,
          body,
          workspaceId,
          itemUuid,
          itemType,
        })
      );
    } else {
      const user = state.user;
      const parentUuid = uuidv4();
      const parentTempItem = generateTempItem({
        position: 0,
        parentId: null,
        parentUuid: null,
        body,
        itemUuid: parentUuid,
        user,
        itemType,
        workspaceId,
      });
      const childTempItem = { ...parentTempItem, position: 1, uuid: itemUuid, parentUuid };

      dispatch({
        type: 'MEETING_ITEM_WITH_CHILD_ADD_REQUEST',
        meetingItemType: itemType,
        payload: [parentTempItem, childTempItem],
      });

      instance
        .post('/api/v0/meeting_items/create_with_child', {
          item: itemWithFieldsForCreate(parentTempItem),
          childItem: itemWithFieldsForCreate(childTempItem),
        })
        .then(json =>
          dispatch({
            type: 'MEETING_ITEM_WITH_CHILD_ADD_RESPONSE',
            meetingItemType: itemType,
            payload: json.data.data,
          })
        )
        .catch(
          () =>
            dispatch({
              type: 'MEETING_ITEM_WITH_CHILD_ADD_ERROR',
              meetingItemType: itemType,
              payload: [parentTempItem, childTempItem],
            }) && WpToastManager.danger(tr('perform.todos.something_went_wrong'))
        );
    }
  };

export const bulkAddMeetingItems =
  ({
    blocks,
    type,
    workspaceId,
    currentUuid,
    parentUuid,
    onFocusedTodoUpdate,
    handleEditorChange,
    isEmptySourceItemBody = false,
  }) =>
  async (dispatch, getState) => {
    const user = getState().user;
    const generatedItems = generateMeetingItemsByDoc({ document: blocks, type, parentUuid, user, workspaceId });
    let firstItem;
    if (isEmptySourceItemBody && handleEditorChange) {
      firstItem = generatedItems.shift();
      handleEditorChange(editorStateForEditorComponent(firstItem.body));
    }

    if (!generatedItems.length) return;

    dispatch({
      type: 'MEETING_ITEM_BULK_ADD_REQUEST',
      payload: {
        items: generatedItems,
        currentUuid,
      },
      meetingItemType: type,
    });

    if (onFocusedTodoUpdate) onFocusedTodoUpdate(last(generatedItems).uuid);

    makeRequest({
      endpoint: '/api/v0/meeting_items/bulk_create',
      method: 'POST',
      dispatch,
      body: {
        items: generatedItems.map(item => ({
          body: item.body,
          itemType: type,
          workspaceId,
          uuid: item.uuid,
          parentUuid: item.parentUuid || currentUuid,
          assignment_attributes: {
            person_id: user.person.id,
            user_id: user.id,
          },
        })),
        workspaceId,
        clientSyncToken: getState().app.clientSyncToken,
        uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
      },
      success: json => {
        dispatch({
          type: 'MEETING_ITEM_BULK_ADD_RESPONSE',
          meetingItemType: type,
          payload: json.data.meeting_items,
        });
      },
      error: () => {
        dispatch({
          type: 'MEETING_ITEM_BULK_ADD_ERROR',
          meetingItemType: type,
          payload: { uuids: generatedItems.map(i => i.uuid) },
        });
      },
    });
  };

export const updateItem =
  (uuid, { itemType, assignmentAttributes: meetingItemAssignmentsAttributes, ...params }) =>
  (dispatch, getState) => {
    const state = getState();
    const app = state.app;
    const hasNewItemTypeParam = has(params, 'newItemType');
    const assignmentParams = {};
    const sourcePage = getSourcePage();

    // if (has(params, 'assignmentPersonId')) {
    //   assignmentParams.assignmentAvatar = null;
    //   assignmentParams.assignee = { personName: null };
    // }

    dispatch({
      type: hasNewItemTypeParam ? 'TOGGLE_ITEM_TYPE' : 'MEETING_ITEM_UPDATE_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, ...params, ...assignmentParams },
    });

    const itemForRequest = hasNewItemTypeParam
      ? { itemType: params.newItemType, dueBy: params.dueBy }
      : { meetingItemAssignmentsAttributes, ...params, ...assignmentParams };

    return makeRequest({
      endpoint: `/api/v0/meeting_items/${uuid}`,
      method: 'PUT',
      dispatch,
      body: {
        item: itemForRequest,
        uuidsToPositions: hasNewItemTypeParam ? uuidsToPositions({ workspace: getState().workspace }) : null,
        clientSyncToken: app.clientSyncToken,
        appId: app.id,
        sourcePage,
      },
      success: json => {
        if (has(params, 'assignmentPersonId')) {
          dispatch({
            type: 'UPDATE_MEETING_ITEM_RESPONSE',
            meetingItemType: itemType,
            payload: json.data,
          });
        }
      },
      error: () => {
        const prevItem = getMeetingItemByUuid(uuid)(state);
        if (prevItem) {
          dispatch({
            type: 'UPDATE_MEETING_ITEM_ERROR',
            meetingItemType: itemType,
            payload: prevItem,
          });
        }
      },
    });
  };

export const moveItemToAnotherTeammate =
  ({ itemUuid, itemType, assignmentId, teammatePersonId, teammateEmail }) =>
  (dispatch, getState) => {
    const state = getState();

    const teammateWorkspace = state.workspaces.find(
      ({ users, isPersonal }) =>
        users.length <= 2 &&
        !isPersonal &&
        users.find(({ personId, email }) => personId === teammatePersonId || email === teammateEmail)
    );

    if (teammateWorkspace) {
      const assignedId =
        teammatePersonId || teammateWorkspace.users.find(({ email }) => email === teammateEmail)?.personId;
      return dispatch(
        updateItem(itemUuid, {
          itemType,
          assignmentAttributes: getAssignmentAttributes({ assignmentId, assignedId }),
          assignmentPersonId: assignedId,
          workspaceId: teammateWorkspace.id,
          removeFromWorkspace: Boolean(state.workspace?.id),
        })
      );
    }
    return dispatch(setOptionsForInviteToTeammateModal({ mentionedNewEmail: teammateEmail, isOpen: true }));
  };

export const toggleTodo =
  ({ id, uuid, itemType }, completed, showNotification = true) =>
  (dispatch, getState) => {
    const state = getState();
    dispatch({
      type: 'MEETING_ITEM_SET_COMPLETED_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, completed, completedBy: state.user },
    });

    const getItemsToComplete = allItems =>
      allItems.filter(item => (item.uuid === uuid || item.parentUuid === uuid) && !item.completed);

    const itemsToComplete = uniqBy(
      [...getItemsToComplete(state.meetingItems), ...getItemsToComplete(state.workspace[listFromType(itemType)])],
      'uuid'
    );

    const sourcePage = getSourcePage();
    return makeRequest({
      endpoint: `/api/v0/meeting_items/${uuid}/toggle${sourcePage ? `?source_page=${sourcePage}` : ''}`,
      method: 'PUT',
      dispatch,
      body: {
        item: { id, uuid, completed },
        clientSyncToken: state.app.clientSyncToken,
      },
      success: json => {
        dispatch({
          type: 'MEETING_ITEM_SET_COMPLETED_RESPONSE',
          meetingItemType: itemType,
          payload: json.data,
        });

        if (completed && showNotification) {
          WpToastManager.success(tr('perform.todos.item_completed'), {
            showDismissBtn: false,
            actionBtnLabel: tr('perform.todos.undo'),
            actionBtnFnc: () =>
              itemsToComplete.map(item => dispatch(toggleTodo({ id: item.id, uuid: item.uuid, itemType }, !completed))),
          });
        }
      },
      error: () => {
        dispatch({
          type: 'MEETING_ITEM_SET_COMPLETED_ERROR',
          meetingItemType: itemType,
          payload: { id, uuid, completed: !completed },
        });
      },
    });
  };

export const moveTodo =
  (type, { draggedItemUuids, hoveredItemUuid, hoverOffset }) =>
  (dispatch, getState) => {
    const { workspace } = getState();

    dispatch({
      type: 'MEETING_ITEM_DROP_REQUEST',
      meetingItemType: type,
      payload: { draggedItemUuids, hoveredItemUuid, hoverOffset },
    });
    return makeRequest({
      endpoint: '/api/v0/meeting_items/sync_positions',
      method: 'PUT',
      dispatch,
      body: {
        uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
        workspaceId: workspace.id,
        clientSyncToken: getState().app.clientSyncToken,
      },
    });
  };

export const nestTodo =
  ({ draggedItemUuids, parentId, parentUuid, itemType, workspaceId, byDrag = false }) =>
  (dispatch, getState) => {
    dispatch({
      type: 'MEETING_ITEM_NEST_REQUEST',
      meetingItemType: itemType,
      payload: {
        draggedItemUuids,
        parentId,
        parentUuid,
        byDrag,
      },
    });

    return makeRequest({
      endpoint: '/api/v0/meeting_items/nest',
      method: 'PUT',
      dispatch,
      body: {
        draggedItemUuids,
        parentUuid,
        workspaceId,
        uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
        clientSyncToken: getState().app.clientSyncToken,
      },
    });
  };

export const nestByTabClick =
  ({ uuid, itemType, workspaceId }) =>
  (dispatch, getState) => {
    const items = getState().workspace[listFromType(itemType)];

    const todo = items.find(t => t.uuid === uuid);
    const childPresented = Boolean(
      items.filter(({ parentUuid: itemParentUuid }) => itemParentUuid === todo.uuid).length
    );
    const index = items.indexOf(todo);
    if (index === 0 || childPresented || todo.parentUuid) return;
    const parentList = items.filter(({ parentUuid: itemParentUuid }) => !itemParentUuid);
    const prevParentItem = parentList[parentList.indexOf(todo) - 1];

    dispatch(
      nestTodo({
        draggedItemUuids: [todo.uuid],
        parentId: prevParentItem.id,
        parentUuid: prevParentItem.uuid,
        itemType,
        workspaceId,
      })
    );
  };

export const removeTodo =
  ({ uuid, itemType, goalSlug, completed, templateSlug }) =>
  (dispatch, getState) => {
    const actionType = 'MEETING_ITEM_REMOVE_REQUEST';
    const state = getState();

    dispatch({
      type: actionType,
      meetingItemType: itemType,
      payload: { uuid, itemType, goalSlug, completed },
      meta: {
        analytics: {
          eventType: EventTypes.track,
          eventPayload: {
            event: 'Deleted item',
            properties: { uuid, itemType, template: templateSlug, app_id: state.app.id },
          },
        },
      },
    });

    return makeRequest({
      endpoint: `/api/v0/meeting_items/${uuid}`,
      method: 'DELETE',
      dispatch,
      body: {
        clientSyncToken: state.app.clientSyncToken,
        uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
      },
      error: () => {
        const prevItem = getMeetingItemByUuid(uuid)(state);
        dispatch({
          type: 'MEETING_ITEM_REMOVE_ERROR',
          meetingItemType: itemType,
          payload: { item: prevItem },
        });
      },
    });
  };

export const handleEmptyItemRemove = uuid => (dispatch, getState) => {
  const state = getState();
  const currentUserId = state.user.id;
  const workspace = state.workspace;
  const { video, commentsCount, user, attachments, itemType, goalSlug, completed, templateSlug } =
    getMeetingItemByUuid(uuid)(state);

  const isChildrenItemsPresented =
    workspace.meeting_items.some(({ parentUuid }) => parentUuid === uuid) ||
    workspace.discussion_topics.some(({ parentUuid }) => parentUuid === uuid);

  if (video && !window.confirm(tr('perform.todos.confirm_delete_video'))) return false;
  if (commentsCount > 0 && !window.confirm(tr('perform.todos.confirm_delete_comments'))) return false;
  if (user && currentUserId !== user.id && !window.confirm(tr('perform.todos.confirm_delete_other_user'))) return false;
  if (isChildrenItemsPresented && !window.confirm(tr('perform.todos.confirm_delete_subitems'))) return false;
  if (attachments.length && !window.confirm(tr('perform.todos.confirm_delete_attachments'))) return false;

  dispatch(removeTodo({ uuid, itemType, goalSlug, completed, templateSlug }));
  return true;
};

export const removeTodoNesting =
  ({ uuid, itemType }) =>
  (dispatch, getState) => {
    dispatch({
      type: 'MEETING_ITEM_REMOVE_NESTING_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, positionField: getPositionFieldForItem() },
    });

    return makeRequest({
      endpoint: `/api/v0/meeting_items/${uuid}/remove_nesting`,
      method: 'PUT',
      dispatch,
      body: {
        item: { uuid },
        uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
        clientSyncToken: getState().app.clientSyncToken,
      },
    });
  };

export const disconnectAsanaTask =
  ({ uuid, itemType }) =>
  (dispatch, getState) => {
    dispatch({
      type: 'MEETING_ITEM_UPDATE_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, asanaData: null },
    });

    return api.disconnectAsanaTaskFromWorkspaceItem({ uuid, clientSyncToken: getState().app.clientSyncToken });
  };

export const createAsanaTask =
  ({ itemUuid, projectId }) =>
  (dispatch, getState) => {
    return api
      .createAsanaTaskFromWorkspaceItem({ itemUuid, projectId, clientSyncToken: getState().app.clientSyncToken })
      .then(response => {
        dispatch({
          type: 'MEETING_ITEM_UPDATE_REQUEST',
          meetingItemType: response.data.data.itemType,
          payload: response.data.data,
        });
      });
  };

export const createServicenowRecord =
  ({ itemUuid, tableName, recordAttributes }) =>
  dispatch => {
    return MeetingItemsAPI.createServicenowRecord(itemUuid, tableName, recordAttributes).then(response => {
      dispatch({
        type: 'MEETING_ITEM_UPDATE_REQUEST',
        meetingItemType: response.data.data.itemType,
        payload: response.data.data,
      });
    });
  };

export const disconnectServicenowRecord =
  ({ uuid, itemType }) =>
  dispatch => {
    dispatch({
      type: 'MEETING_ITEM_UPDATE_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, servicenowData: null },
    });

    return MeetingItemsAPI.disconnectServicenowRecord(uuid);
  };

export const createJiraIssue =
  ({ itemUuid, projectId, issueTypeId, additionalFieldsValues }) =>
  (dispatch, getState) => {
    return MeetingItemsAPI.createJiraIssue(itemUuid, {
      projectId,
      issueTypeId,
      additionalFieldsValues,
      clientSyncToken: getState().app.clientSyncToken,
    }).then(response => {
      dispatch({
        type: 'MEETING_ITEM_UPDATE_REQUEST',
        meetingItemType: response.data.data.itemType,
        payload: response.data.data,
      });
    });
  };

export const disconnectJiraIssue =
  ({ uuid, itemType }) =>
  dispatch => {
    dispatch({
      type: 'MEETING_ITEM_UPDATE_REQUEST',
      meetingItemType: itemType,
      payload: { uuid, jiraData: null },
    });

    return MeetingItemsAPI.disconnectJiraIssue(uuid);
  };

export const addItemToGoal =
  ({ itemUuid, goalId }) =>
  (dispatch, getState) => {
    return api
      .addWorkspaceItemToGoal({ itemUuid, goalId, clientSyncToken: getState().app.clientSyncToken })
      .then(response => {
        if (response.data.moved_workspaces) {
          // If this action moved the item to a different workspace, utilize the duplicate reducer
          // to ensure the correct behavior.
          dispatch({
            type: 'DUPLICATE_ACROSS_WORKSPACES_RESPONSE',
            payload: { data: [response.data.data], itemUuids: [itemUuid], isMoveItems: true },
          });
        } else {
          // If the item didn't move workspaces, all we need to do is update it in the store
          dispatch({
            type: 'UPDATE_MEETING_ITEM_RESPONSE',
            payload: response.data.data,
            meetingItemType: response.data.data.itemType,
          });
        }
        WpToastManager.success(tr('perform.todos.added_to_goal'));
      })
      .catch(() => WpToastManager.danger(tr('perform.todos.something_went_wrong_try_again')));
  };

export const bulkUpdate = (selectedUuids, params) => (dispatch, getState) => {
  const state = getState();
  const {
    app: { id: appId },
  } = state;
  const sourcePage = getSourcePage();
  const selectedMeetingItems = getItemsByUuids(selectedUuids)(state);
  let selectedMeetingItemsUuids = selectedMeetingItems.map(({ uuid }) => uuid);
  dispatch({
    type: 'MEETING_ITEM_BULK_UPDATE_REQUEST',
    payload: { selectedUuids: selectedMeetingItemsUuids, params },
  });

  if (has(params, 'assignmentPersonId')) {
    // eslint-disable-next-line no-param-reassign
    params.meeting_item_assignments_attributes = [];
    if (params.assignmentPersonId) {
      params.meeting_item_assignments_attributes.push({ person_id: params.assignmentPersonId });
      selectedMeetingItemsUuids = selectedMeetingItems
        .filter(({ assignmentPersonId }) => assignmentPersonId !== params.assignmentPersonId)
        .map(({ uuid }) => uuid);
    }
    // eslint-disable-next-line no-param-reassign
    delete params.assignmentPersonId;
  }

  return makeRequest({
    endpoint: '/api/v0/meeting_items/bulk_update',
    method: 'PUT',
    dispatch,
    body: {
      uuids: selectedMeetingItemsUuids,
      item: { ...params },
      clientSyncToken: getState().app.clientSyncToken,
      appId,
      sourcePage,
    },
    success: json => {
      dispatch({
        type: 'BULK_UPDATE_MEETING_ITEM_RESPONSE',
        payload: json.data,
      });
      if (params.completed) {
        WpToastManager.success(tr('perform.todos.item_completed'), {
          showDismissBtn: false,
          actionBtnLabel: tr('perform.todos.undo'),
          actionBtnFnc: () => dispatch(bulkUpdate(selectedUuids, { completed: !params.completed })),
        });
      }
    },
  });
};

export const bulkRemove = selectedUuids => (dispatch, getState) => {
  dispatch({
    type: 'MEETING_ITEM_BULK_REMOVE_REQUEST',
    payload: { selectedUuids },
  });

  return makeRequest({
    endpoint: '/api/v0/meeting_items/bulk_destroy',
    method: 'DELETE',
    dispatch,
    body: {
      uuids: selectedUuids,
      clientSyncToken: getState().app.clientSyncToken,
      uuidsToPositions: uuidsToPositions({ workspace: getState().workspace }),
    },
  });
};

export const addMeetingItemAttachment =
  ({ uuid, file, itemType, attachmentUuid }) =>
  async dispatch => {
    const data = new FormData();
    data.append('file', file);
    data.append('attachmentUuid', attachmentUuid);
    try {
      const response = await instance.putForm(`/api/v0/meeting_items/${uuid}/create_attachment`, data);

      if (response && response.status === 200) {
        dispatch({
          type: 'ADD_MEETING_ITEM_ATTACHMENT_RESPONSE',
          meetingItemType: itemType,
          payload: { uuid, attachment: response.data.attachment },
        });
      } else {
        WpToastManager.danger(tr('perform.todos.something_went_wrong'));
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        if (error.response.data.error === 'File content type is invalid') {
          WpToastManager.danger(tr('perform.todos.unsupported_file_type'));
        } else {
          WpToastManager.danger(error.response.data.error);
        }
      } else {
        WpToastManager.danger(tr('perform.todos.something_went_wrong_try_again'));
      }
    }
  };

export const upsertMeetingItemRecurrence =
  ({ workspaceItemUuid, itemType, isEdit, ...recurrenceOptions }) =>
  async (dispatch, getState) => {
    try {
      const response = await api.upsertRecurringWorkspaceItem({
        workspaceItemUuid,
        client_sync_token: getState().app.clientSyncToken,
        ...recurrenceOptions,
      });
      if (response && response.status === 200) {
        dispatch({
          type: 'UPDATE_MEETING_ITEM_RECURRENCE',
          meetingItemType: itemType,
          payload: {
            uuid: workspaceItemUuid,
            recurringWorkspaceItem: recurrenceOptions,
            isEdit,
          },
        });
      } else {
        WpToastManager.danger(response.error);
      }
    } catch (error) {
      WpToastManager.danger(error?.response?.data?.error || tr('perform.todos.something_went_wrong_try_again'));
    }
  };

export const deleteMeetingItemRecurrence =
  ({ workspaceItemUuid, itemType }) =>
  async (dispatch, getState) => {
    try {
      const response = await api.deleteRecurringWorkspaceItem({
        workspaceItemUuid,
        client_sync_token: getState().app.clientSyncToken,
      });

      if (response && response.status === 200) {
        dispatch({
          type: 'REMOVE_MEETING_ITEM_RECURRENCE',
          meetingItemType: itemType,
          payload: {
            uuid: workspaceItemUuid,
            recurringWorkspaceItem: null,
          },
        });
      } else {
        WpToastManager.danger(response.error);
      }
    } catch (error) {
      WpToastManager.danger(error?.response?.data?.error || tr('perform.todos.something_went_wrong_try_again'));
    }
  };

export const setViewedWorkspaceItemComments = itemUuid => (dispatch, getState) => {
  const workspace = getState().workspace;
  const modifiedCommentsUuids = workspace.workspaceNotifications?.modifiedCommentsUuids || [];
  if (!modifiedCommentsUuids.length) return;

  const item = [...workspace.discussion_topics, ...workspace.meeting_items].find(({ uuid }) => uuid === itemUuid);
  if (!item) return;

  const comments = item.comments;
  const newCommentsUuids = modifiedCommentsUuids.filter(commentUuid =>
    comments?.find(({ uuid }) => uuid === commentUuid)
  );

  if (newCommentsUuids.length)
    dispatch({
      type: 'VIEW_NEW_WORKSPACE_COMMENT',
      notificationField: 'modifiedCommentsUuids',
      payload: newCommentsUuids,
    });
};

export function setViewedWorkspaceItem(itemUuid) {
  return (dispatch, getState) => {
    const workspace = getState().workspace;
    const childUuids = [...workspace.discussion_topics, ...workspace.meeting_items]
      .filter(({ parentUuid }) => parentUuid === itemUuid)
      .map(({ uuid }) => uuid);
    const viewedItemsUuids = [itemUuid, ...childUuids];
    dispatch({
      type: 'VIEW_NEW_WORKSPACE_ITEM',
      notificationField: 'modifiedItemsUuids',
      payload: viewedItemsUuids,
    });
    viewedItemsUuids.map(uuid => dispatch(setViewedWorkspaceItemComments(uuid)));
  };
}
